import {
    Component,
    forwardRef,
    OnDestroy,
    OnInit,
    Injector,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    inject,
    input,
    output,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NgControl,
} from '@angular/forms';
import {
    EntityFilter,
    EntityFilterCollection,
    EntityFilterConstants,
} from '../../../admin/models/entity-filter';
import { EntityFilterType } from '../../../shared-platform/models/constants/entity-filter-type';
import { SubSink } from 'subsink';
import { UserService } from '../../../admin/services/user.service';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { DropDownButtonModule } from '@progress/kendo-angular-buttons';
import { PermissionNames } from '../../../admin/models/permission-names';

/**
 * Single-selection entity filter component
 * - Always maintains ONE entity selected (cannot be null after initialization)
 * - Selecting a new entity automatically replaces the previous selection
 * - Default selection: First Association on page load
 * - Clicking the same entity does nothing (no unselect)
 * - Emits entityChanged event with complete EntityFilter when selection changes
 *
 * Usage:
 * <app-single-selection-entity
 *     formControlName="dataSource"
 *     [hideNetwork]="true"
 *     [hideShipper]="true"
 *     (entityChanged)="onEntityChanged($event)">
 * </app-single-selection-entity>
 */
@Component({
    selector: 'app-single-selection-entity',
    templateUrl: './single-selection-entity.component.html',
    styleUrls: ['./single-selection-entity.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipsModule, DropDownButtonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SingleSelectionEntityComponent),
            multi: true,
        },
    ],
})
export class SingleSelectionEntityComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    private userService = inject(UserService);
    private injector = inject(Injector);
    private cdr = inject(ChangeDetectorRef);

    /** Controls whether to hide Network selection (default: visible) */
    public readonly hideNetwork = input<boolean>(false);

    /** Controls whether to hide Shipper selection (default: visible) */
    public readonly hideShipper = input<boolean>(false);

    public readonly associationTooltip = input<string | undefined>('');

    /** Emits the complete EntityFilter (entityId, name, entityFilterType) when selection changes */
    readonly entityChanged = output<EntityFilter>();

    public filterCollection: EntityFilterCollection =
        new EntityFilterCollection();
    public selectedFilter: EntityFilter | null = null;

    private hasShippersAccess: boolean = false;
    private isInitialized: boolean = false;
    private sub = new SubSink();
    private onChange: (value: EntityFilter | null) => void = () => {};
    private onTouched: () => void = () => {};

    get hasSingleAssociation(): boolean {
        return this.filterCollection.associations.length === 1;
    }

    get hasMultipleAssociations(): boolean {
        return this.filterCollection.associations.length > 1;
    }

    get hasSingleCustomer(): boolean {
        return (
            this.canShowShippers && this.filterCollection.customers.length === 1
        );
    }

    get hasMultipleCustomers(): boolean {
        return (
            this.canShowShippers && this.filterCollection.customers.length > 1
        );
    }

    get isAssociationSelected(): boolean {
        return this.isSelectedType(EntityFilterType.Association);
    }

    get isCustomerSelected(): boolean {
        return this.isSelectedType(EntityFilterType.Shipper);
    }

    get isNetworkSelected(): boolean {
        return this.isSelectedType(EntityFilterType.Network);
    }

    get associationToggleText(): string {
        const single = this.hasSingleAssociation
            ? this.filterCollection.associations[0]
            : undefined;
        return this.getToggleText(
            EntityFilterType.Association,
            single,
            'Select Association',
        );
    }

    get customerToggleText(): string {
        const single = this.hasSingleCustomer
            ? this.filterCollection.customers[0]
            : undefined;
        return this.getToggleText(
            EntityFilterType.Shipper,
            single,
            'Select Shipper',
        );
    }

    get selectedFilterText(): string {
        if (!this.selectedFilter) return '';

        switch (this.selectedFilter.entityFilterType) {
            case EntityFilterType.Shipper:
            case EntityFilterType.Association:
                return `${this.selectedFilter.name} Data`;
            case EntityFilterType.Network:
                return 'Network Data';
            default:
                return this.selectedFilter.name;
        }
    }

    private get canShowShippers(): boolean {
        return !this.hideShipper() && this.hasShippersAccess;
    }

    ngOnInit(): void {
        // Listen for external value changes on the parent form control
        const control = this.injector.get(NgControl, null)?.control;
        if (control) {
            this.sub.sink = control.valueChanges.subscribe((value) =>
                this.writeValue(value),
            );
        }

        this.sub.sink = this.userService.getCurrentUser().subscribe((user) => {
            if (!user) {
                return;
            }

            this.hasShippersAccess = user.hasPermission(
                PermissionNames.RailIndustry.Shippers.Read,
            );

            const filters: EntityFilter[] = [];

            if (this.canShowShippers) {
                user.shippers.forEach((shipper) => {
                    filters.push(
                        new EntityFilter(
                            shipper.globalId,
                            shipper.name,
                            EntityFilterType.Shipper,
                        ),
                    );
                });
            }

            user.associations.forEach((association) => {
                filters.push(
                    new EntityFilter(
                        association.acronym,
                        association.name,
                        EntityFilterType.Association,
                    ),
                );
            });

            if (!this.hideNetwork()) {
                filters.push(EntityFilterConstants.Network);
            }

            // Sort alphabetically and remove duplicates
            filters.sort((a, b) => a.name.localeCompare(b.name));
            const distinctFilters = [
                ...new Map(filters.map((f) => [f.entityId, f])).values(),
            ];

            this.filterCollection = new EntityFilterCollection(
                ...distinctFilters,
            );

            if (!this.isInitialized) {
                this.isInitialized = true;
                Promise.resolve().then(() => {
                    if (!this.selectedFilter) {
                        this.setDefaultSelection();
                    }
                });
            }

            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    getFilterTooltip(filter: EntityFilter): string {
        switch (filter.entityFilterType) {
            case EntityFilterType.Shipper:
                return 'Shipper specific data.';
            case EntityFilterType.Association:
                return (
                    this.associationTooltip() ||
                    'Anonymized and Aggregate association data.'
                );
            case EntityFilterType.Network:
                return 'Anonymized and Aggregate network data.';
            default:
                return '';
        }
    }

    isSelected(filter: EntityFilter): boolean {
        return this.selectedFilter?.entityId === filter.entityId;
    }

    /**
     * Select a filter, automatically replacing any previous selection.
     * Selecting the already-selected filter does nothing.
     */
    select(filter: EntityFilter): void {
        if (this.isSelected(filter)) {
            return;
        }

        this.selectedFilter = filter;

        this.onChange(this.selectedFilter);
        this.onTouched();
        this.entityChanged.emit(filter);
    }

    writeValue(
        value: EntityFilter | EntityFilterCollection | null | undefined,
    ): void {
        this.selectedFilter = this.resolveSelection(value);
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: EntityFilter | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * A collection resolves to its first Shipper (when visible), then first Association,
     * then Network (when visible). A single EntityFilter is used as-is.
     */
    private resolveSelection(
        value: EntityFilter | EntityFilterCollection | null | undefined,
    ): EntityFilter | null {
        if (value instanceof EntityFilterCollection) {
            if (this.canShowShippers && value.customers.length > 0) {
                return value.customers[0];
            }
            if (value.associations.length > 0) {
                return value.associations[0];
            }
            if (!this.hideNetwork() && value.network) {
                return value.network;
            }
            return null;
        }

        return value instanceof EntityFilter ? value : null;
    }

    /**
     * Priority: First Association > Network (if not hidden) > First Shipper (if not hidden)
     */
    private setDefaultSelection(): void {
        const defaultFilter =
            this.filterCollection.associations[0] ??
            (!this.hideNetwork() ? this.filterCollection.network : undefined) ??
            (!this.hideShipper()
                ? this.filterCollection.customers[0]
                : undefined);

        if (defaultFilter) {
            this.select(defaultFilter);
        }
    }

    private isSelectedType(type: string): boolean {
        return this.selectedFilter?.entityFilterType === type;
    }

    private getToggleText(
        type: string,
        single: EntityFilter | undefined,
        placeholder: string,
    ): string {
        if (this.selectedFilter && this.isSelectedType(type)) {
            return `${this.selectedFilter.name} Data`;
        }
        if (single) {
            return `${single.name} data`;
        }
        return placeholder;
    }
}
